import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Box, Button, Typography, MuiThemeProvider } from '@material-ui/core'
import ErrorIcon from 'assets/icons/ErrorIcon'
import { useHistory, useParams } from 'react-router-dom'
import { materialAtlasTheme } from 'material-atlas/theme'
import { MuiPickersUtilsProvider } from '@material-ui/pickers'
import MomentUtils from '@date-io/moment'
import moment from 'moment'
import LinearIndeterminate from 'components/shared/LinearProgress'
import ConfirmationDialogGeneric from 'components/shared/ConfirmationDialogGeneric'
import {
    PageLayout,
    PageBody,
    PageHeader,
    MinutesManagerToolbar
} from 'components/layout/page-layout'
import { AtlasButton } from 'components/atlas-material/button'
import { AtlasTooltip } from 'components/atlas-components/atlas-tooltip/Tooltip'
import i18n from 'common/util/internationalization'
import emitter from 'common/util/events'
import authenticationManager from 'businesslayer/authenticationManager'
import {
    getAIStyleById,
    saveWritingStylePrompt,
    generateWritingStyles
} from 'businesslayer/networkrequests/AIStylesService'
import { useCommitteesState } from 'components/contexts/committee-context'
import { WRITING_STYLE_STATUS } from 'components/minutetaker/components/InvitationView/types'
import { useMinutesPolling } from 'common/customHooks/pollingApiStatus'
import { POLLING_INTERVAL, MAX_WRITING_STYLE_DESCRIPTION_LENGTH, TOAST_TYPE_SUCCESS, TOAST_TYPE_FAIL, WARNING_TYPE_SPELLING, WARNING_TYPE_PROFANITY } from 'common/constants/constants'
import StatusToast from 'components/atlas-components/atlas-toast/Toast'
import { ToastNotification } from 'components/atlas-components/atlas-toast/ToastNotification'
import { CircularProgressLoader } from 'components/global-progress-modal/global-progress-modal'
import { findSecurityHighlights, findHTMLHighlights, findProfanityHighlights } from './writingStylePromptGuard'
import {
    adjustHighlightsForEdit,
    getHardBlockI18nKeys,
    sanitizeContentWarnings
} from './writingStyleValidationTypes'
import type { ContentWarning } from './writingStyleValidationTypes'
import HighlightedTextarea from './HighlightedTextarea'
import { useWritingStylesStyles } from './writing-styles.styles'
import RegenerateIcon from 'assets/icons/RegenerateIcon'
import BackButtonIcon from 'assets/icons/BackButtonIcon'
import { SaveIcon } from 'assets/icons/SaveIcon'
import TextLengthCounter from 'components/shared/TextLengthCounter'

interface StyleDetailAttributes {
    name?: string
    generated_style?: string
    source_minutes_title?: string
    style_generated_at?: string
    writing_style_status?: string
}

export const WritingStyleDetailPage = () => {
    const classes = useWritingStylesStyles()
    const history = useHistory()
    const { styleId } = useParams<{ styleId: string }>()
    const { adminCommittees } = useCommitteesState()

    const committeeIds = useMemo(() => adminCommittees.map((c) => +c.id).filter((id) => id > 0), [
        adminCommittees
    ])

    const [attributes, setAttributes] = useState<StyleDetailAttributes | null>(null)
    const [draft, setDraft] = useState('')
    const [initialDraft, setInitialDraft] = useState('')
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [saveError, setSaveError] = useState<string | null>(null)
    const [hardBlockErrors, setHardBlockErrors] = useState<string[]>([])
    const [clientBlockErrors, setClientBlockErrors] = useState<string[]>([])
    const [highlightRanges, setHighlightRanges] = useState<ContentWarning[]>([])
    const [showRegenerateConfirmation, setShowRegenerateConfirmation] = useState(false)
    const [showBackConfirmation, setShowBackConfirmation] = useState(false)
    const [regenerating, setRegenerating] = useState(false)
    const [showPromptSaveToast, setShowPromptSaveToast] = useState(false)
    const [showPromptSaveFailToast, setShowPromptSaveFailToast] = useState(false)
    const [highlightRevision, setHighlightRevision] = useState(0)

    const {
        writingStyleDocuments,
        isPolling,
        setIsPolling,
        fetchMinutesDocuments: fetchPollingMinutesDocuments,
        toasts,
        setToasts
    } = useMinutesPolling()

    // Tracks the last-seen status for this style so we can detect the PROGRESS → done transition
    const prevStyleStatusRef = useRef<string>('')
    const unblockRef = useRef<(() => void) | null>(null)
    // True when the confirmation dialog was triggered by the browser back button (POP)
    const isPendingPopRef = useRef(false)
    const validationDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    const isDirty = draft !== initialDraft
    const isEmptyDraft = draft.trim() === ''
    const isOverMaxLength = draft.length > MAX_WRITING_STYLE_DESCRIPTION_LENGTH
    const hasGuardrailError =
        highlightRanges.some((h) => h.type === 'guardrail') ||
        hardBlockErrors.includes(i18n.t('STYLE_PROMPT_GUARDRAIL_ERROR') as string)
    const hasProfanityWarnings = !hasGuardrailError && highlightRanges.some((h) => h.type === WARNING_TYPE_PROFANITY)

    /**
     * True when the style is being regenerated — either triggered from this page
     * or detected via writing_style_status from the API (e.g. triggered from the list page).
     * Used to lock the description field and action buttons while keeping Back navigable.
     */
    const isRegenerating =
        regenerating || attributes?.writing_style_status === WRITING_STYLE_STATUS.PROGRESS

    useEffect(() => {
        return () => {
            if (validationDebounceRef.current) clearTimeout(validationDebounceRef.current)
        }
    }, [])

    const adjustedHighlightsRef = useRef<ContentWarning[]>([])

    const setHighlights = useCallback((highlights: ContentWarning[]) => {
        adjustedHighlightsRef.current = highlights
        setHighlightRanges(highlights)
    }, [])

    const clearValidationState = useCallback(() => {
        setSaveError(null)
        setHardBlockErrors([])
        setClientBlockErrors([])
        setHighlights([])
    }, [setHighlights])

    const load = useCallback(async () => {
        if (!styleId) {
            return
        }
        setLoading(true)
        setError(null)
        try {
            const res = await getAIStyleById(styleId)
            const attrs = res?.data?.attributes as StyleDetailAttributes | undefined
            if (!attrs) {
                setError(i18n.t('WRITING_STYLE_DETAIL_LOAD_ERROR') as string)
                return
            }
            setAttributes(attrs)
            const text = (attrs.generated_style || '').replace(/\r\n/g, '\n')
            setDraft(text)
            setInitialDraft(text)
        } catch (e: any) {
            if (e?.status === 404) {
                history.push('/not-found')
                return
            }
            setError(i18n.t('WRITING_STYLE_DETAIL_LOAD_ERROR') as string)
        } finally {
            setLoading(false)
        }
    }, [styleId, history])

    useEffect(() => {
        load()
    }, [load])

    // Signal the navbar to show all admin icons (attendance, action dashboard, templates).
    // Required on direct page load/refresh since the list page won't have mounted to emit this.
    useEffect(() => {
        emitter.emit('canAddMinutes')
    }, [])

    useEffect(() => {
        let intervalId: number | undefined
        if (isPolling) {
            fetchPollingMinutesDocuments()
            intervalId = setInterval(fetchPollingMinutesDocuments, POLLING_INTERVAL) as number
        }
        return () => {
            if (intervalId) {
                clearInterval(intervalId)
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isPolling])

    // Detect when this style's status transitions PROGRESS → done, then unlock UI and reload data
    useEffect(() => {
        if (!writingStyleDocuments || writingStyleDocuments.length === 0) {
            return
        }
        const styleDoc = (writingStyleDocuments as any[]).find(
            (doc) => String(doc.id) === String(styleId)
        )
        const currentStatus = styleDoc?.attributes?.writing_style_status || ''
        const prevStatus = prevStyleStatusRef.current

        if (
            prevStatus === WRITING_STYLE_STATUS.PROGRESS &&
            currentStatus !== WRITING_STYLE_STATUS.PROGRESS &&
            currentStatus
        ) {
            setRegenerating(false)
            // Clear save-validation errors left over from before regeneration started
            clearValidationState()
            load()
        }

        if (currentStatus) {
            prevStyleStatusRef.current = currentStatus
        }
    }, [writingStyleDocuments, styleId, load, clearValidationState])

    // Intercept the browser back button (POP) when there are unsaved changes.
    // Returning false blocks silently — no native window.confirm appears.
    // During regeneration we skip the block — the new prompt will replace any edits.
    useEffect(() => {
        if (!isDirty || isRegenerating) return

        const unblock = history.block((_, action) => {
            if (action !== 'POP') return
            isPendingPopRef.current = true
            setShowBackConfirmation(true)
            return false
        })
        unblockRef.current = unblock

        return () => {
            unblock()
            unblockRef.current = null
        }
    }, [isDirty, isRegenerating, history])

    const handleBack = useCallback(() => {
        if (isDirty && !isRegenerating) {
            setShowBackConfirmation(true)
            return
        }
        history.push('/writing-style-library')
    }, [history, isDirty, isRegenerating])

    const handleBackConfirm = useCallback(() => {
        setShowBackConfirmation(false)
        if (isPendingPopRef.current) {
            isPendingPopRef.current = false
            unblockRef.current?.()
            unblockRef.current = null
            history.goBack()
        } else {
            history.push('/writing-style-library')
        }
    }, [history])

    const handleFocus = useCallback(() => {
        const profanityHighlights = findProfanityHighlights(draft)
        const serverHighlights = highlightRanges.filter((h) => h.type !== WARNING_TYPE_PROFANITY)
        const merged = [
            ...serverHighlights,
            ...profanityHighlights.filter(
                (p) => !serverHighlights.some((s) => p.offset < s.offset + s.length && p.offset + p.length > s.offset)
            )
        ].sort((a, b) => a.offset - b.offset)
        setHighlights(merged)
    }, [draft, highlightRanges, setHighlights])

    const handleDraftChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            const newText = e.target.value.replace(/\r\n/g, '\n')
            setSaveError(null)

            const adjusted = adjustHighlightsForEdit(adjustedHighlightsRef.current, draft, newText)

            // Clear an error banner only once every highlight of that type is gone
            if (!adjusted.some((h) => h.type !== WARNING_TYPE_SPELLING && h.type !== WARNING_TYPE_PROFANITY)) setHardBlockErrors([])
            const serverHighlights = adjusted.filter((h) => h.type !== WARNING_TYPE_PROFANITY)
            const profanityHighlights = findProfanityHighlights(newText).filter(
                (p) =>
                    !serverHighlights.some(
                        (s) => p.offset < s.offset + s.length && p.offset + p.length > s.offset
                    )
            )
            const withProfanity = [...serverHighlights, ...profanityHighlights].sort(
                (a, b) => a.offset - b.offset
            )
            setHighlights(withProfanity)

            setDraft(newText)

            if (validationDebounceRef.current) clearTimeout(validationDebounceRef.current)
            validationDebounceRef.current = setTimeout(() => {
                const securityHighlights = findSecurityHighlights(newText)
                const htmlHighlights = findHTMLHighlights(newText)
                const newClientHighlights = [...securityHighlights, ...htmlHighlights].sort(
                    (a, b) => a.offset - b.offset
                )

                const surviving = adjustedHighlightsRef.current
                const survivingSpelling = surviving.filter((h) => h.type === WARNING_TYPE_SPELLING)
                const survivingProfanity = surviving.filter((h) => h.type === WARNING_TYPE_PROFANITY)
                // Server-only types (sql_injection, xss, guardrail, etc.) that the client
                // cannot re-detect — keep them until the user edits their exact range.
                const survivingServerOnly = surviving.filter(
                    (h) => h.type !== WARNING_TYPE_SPELLING && h.type !== WARNING_TYPE_PROFANITY && h.type !== 'html_tag'
                )

                const merged = [
                    ...survivingSpelling,
                    ...survivingProfanity,
                    ...survivingServerOnly,
                    ...newClientHighlights
                ].sort((a, b) => a.offset - b.offset)

                setHighlights(merged)
                if (newClientHighlights.length > 0) {
                    setClientBlockErrors([i18n.t('STYLE_PROMPT_HTML_CONTENT_ERROR') as string])
                } else {
                    setClientBlockErrors([])
                }
            }, 300)
        },
        [draft, setHighlights]
    )

    const handleSave = useCallback(async () => {
        clearValidationState()

        const trimmedDraft = draft.trim()
        setDraft(trimmedDraft)

        if (!styleId) {
            return
        }

        setSaving(true)
        try {
            const result = await saveWritingStylePrompt(styleId, trimmedDraft)

            if (!result.ok) {
                setHighlightRevision((r) => r + 1)
                const apiHighlights = sanitizeContentWarnings(
                    result.validation.meta?.content_warnings ?? [],
                    trimmedDraft.length
                )
                const profanityHighlights = findProfanityHighlights(trimmedDraft).filter(
                    (p) => !apiHighlights.some(
                        (s) => p.offset < s.offset + s.length && p.offset + p.length > s.offset
                    )
                )
                setHighlights(
                    [...apiHighlights, ...profanityHighlights].sort((a, b) => a.offset - b.offset)
                )
                setHardBlockErrors(
                    getHardBlockI18nKeys(result.validation).map((key) => i18n.t(key) as string)
                )
                setShowPromptSaveFailToast(true)
                return
            }

            setInitialDraft(trimmedDraft)
            setShowPromptSaveToast(true)
        } catch {
            setShowPromptSaveFailToast(true)
        } finally {
            setSaving(false)
        }
    }, [clearValidationState, draft, setHighlights, styleId])

    const handleRegenerateClick = useCallback(() => {
        setShowRegenerateConfirmation(true)
    }, [])

    const handleRegenerateConfirm = useCallback(async () => {
        if (!styleId) {
            return
        }
        setShowRegenerateConfirmation(false)
        // Clear any lingering save-validation state — regeneration replaces the prompt,
        // so errors from the previous manual edit are irrelevant.
        clearValidationState()
        setRegenerating(true)
        // Seed the ref so the polling transition-detector knows we were in PROGRESS
        prevStyleStatusRef.current = WRITING_STYLE_STATUS.PROGRESS
        try {
            const result = await generateWritingStyles(committeeIds, styleId)
            const status = result?.data?.[0]?.attributes?.writing_style_status
            if (status === WRITING_STYLE_STATUS.PROGRESS) {
                // Common async path — start polling; the transition effect will unlock UI and
                // show the toast once the backend reports success or failure
                setIsPolling(true)
            } else if (status) {
                // Rare synchronous completion — clear errors and reload
                setRegenerating(false)
                clearValidationState()
                await load()
            }
        } catch {
            setRegenerating(false)
            setShowPromptSaveFailToast(true)
        }
    }, [clearValidationState, committeeIds, load, setIsPolling, styleId])

    const removeToast = useCallback(
        (toastId: number | string) => {
            setToasts((prev) => prev.filter((t) => t.id !== toastId))
        },
        [setToasts]
    )

    const styleGeneratedAt = attributes?.style_generated_at
    const generatedLabel = useMemo(
        () => (styleGeneratedAt ? moment(styleGeneratedAt).format('LL') : ''),
        [styleGeneratedAt]
    )

    return (
        <MuiThemeProvider theme={materialAtlasTheme}>
            <MuiPickersUtilsProvider utils={MomentUtils}>
                <PageLayout>
                    <PageHeader
                        title={i18n.t('PAGE_TITLE_MM')}
                        onSignout={authenticationManager.handleSignout}>
                        <MinutesManagerToolbar />
                    </PageHeader>
                    <LinearIndeterminate loading={loading} />

                    <Box className={classes.header}>
                        <AtlasTooltip title={i18n.t('BACK') as string} placement="bottom">
                            <span>
                                <Button
                                    disableFocusRipple
                                    disableRipple
                                    data-analytics="MM-WritingStyles-Back-DetailPage"
                                    className={classes.detailBackButton}
                                    onClick={handleBack}
                                    aria-label={i18n.t('BACK') as string}>
                                    <BackButtonIcon title={i18n.t('BACK') as string} />
                                </Button>
                            </span>
                        </AtlasTooltip>
                        <Box className={classes.detailHeaderTitleBox}>
                            <Typography
                                variant="h1"
                                component="h1"
                                className={classes.detailPageTitle}>
                                {styleId === '0'
                                    ? i18n.t('DILIGENT_AI_STYLE')
                                    : i18n.t('CUSTOMISED_AI_STYLE')}
                            </Typography>
                        </Box>
                        <AtlasButton
                            disableRipple
                            data-analytics="MM-WritingStyles-Regenerate-DetailPage"
                            className={`${classes.detailToolbarTextButton} ${classes.detailRegenerateSpacing}`}
                            startIcon={<RegenerateIcon className={classes.detailRegenerateIcon} />}
                            onClick={handleRegenerateClick}
                            disabled={isRegenerating || loading || saving}>
                            {i18n.t('WRITING_STYLE_REGENERATE_BUTTON')}
                        </AtlasButton>
                        <AtlasButton
                            disableRipple
                            data-analytics="MM-WritingStyles-Save-DetailPage"
                            className={classes.generateStyleButton}
                            startIcon={<SaveIcon className={classes.detailSaveIcon} />}
                            onClick={() => handleSave()}
                            disabled={
                                saving ||
                                !isDirty ||
                                isRegenerating ||
                                isEmptyDraft ||
                                isOverMaxLength ||
                                clientBlockErrors.length > 0
                            }>
                            {i18n.t('SAVE')}
                        </AtlasButton>
                    </Box>

                    <Box className={classes.detailContentArea}>
                        <PageBody>
                            <Box className={classes.detailContentWrapper}>
                                {saving && (
                                    <Box
                                        className={classes.savingOverlay}
                                        role="status"
                                        aria-live="polite">
                                        <Box className={classes.savingOverlayInner}>
                                            <CircularProgressLoader />
                                            <span className={classes.savingOverlayLabel}>
                                                {i18n.t('WRITING_STYLE_VALIDATING_PROMPT')}
                                            </span>
                                        </Box>
                                    </Box>
                                )}
                                {error && (
                                    <Typography color="error" className={classes.detailLoadError}>
                                        {error}
                                    </Typography>
                                )}

                                {!loading && !error && (
                                    <>
                                        {attributes?.source_minutes_title && (
                                            <Typography
                                                variant="body2"
                                                className={classes.detailSourceLabel}>
                                                {i18n.t('STYLE_PROMPT_SOURCE_DOC_LABEL', {
                                                    title: attributes.source_minutes_title
                                                })}
                                            </Typography>
                                        )}
                                        {generatedLabel && (
                                            <Typography
                                                variant="body2"
                                                className={classes.detailDateLabel}>
                                                {i18n.t('STYLE_PROMPT_GENERATED_DATE_LABEL', {
                                                    date: generatedLabel
                                                })}
                                            </Typography>
                                        )}
                                        <Typography
                                            component="h3"
                                            className={classes.detailStyleDescriptionHeading}>
                                            {i18n.t('STYLE_PROMPT_DESCRIPTION_LABEL')}
                                        </Typography>
                                        <Box
                                            className={
                                                isRegenerating
                                                    ? classes.detailPromptFieldWrapperRegenerating
                                                    : undefined
                                            }>
                                            {isRegenerating && (
                                                <Box
                                                    className={classes.regeneratingSpinnerOverlay}
                                                    role="status"
                                                    aria-live="polite">
                                                    <Box className={classes.savingOverlayInner}>
                                                        <CircularProgressLoader />
                                                        <span
                                                            className={classes.savingOverlayLabel}>
                                                            {i18n.t(
                                                                'WRITING_STYLE_REGENERATING_STYLE'
                                                            )}
                                                        </span>
                                                    </Box>
                                                </Box>
                                            )}
                                            <HighlightedTextarea
                                                value={draft}
                                                onChange={handleDraftChange}
                                                disabled={isRegenerating}
                                                highlights={highlightRanges}
                                                scrollRevision={highlightRevision}
                                                ariaLabel={
                                                    i18n.t(
                                                        'STYLE_PROMPT_DESCRIPTION_LABEL'
                                                    ) as string
                                                }
                                                onFocus={handleFocus}
                                                describedById={
                                                    hardBlockErrors.length > 0
                                                        ? 'writing-style-hard-block-errors'
                                                        : hasProfanityWarnings
                                                        ? 'writing-style-profanity-warnings'
                                                        : undefined
                                                }
                                            />
                                        </Box>
                                        <Box className={classes.counterRow}>
                                            <Box className={classes.errorStack}>
                                                {isOverMaxLength && (
                                                    <Box className={classes.detailSaveErrorRow}>
                                                        <ErrorIcon />
                                                        <Typography
                                                            className={classes.detailSaveError}>
                                                            {
                                                                i18n.t(
                                                                    'STYLE_PROMPT_MAX_LENGTH_ERROR'
                                                                )
                                                            }
                                                        </Typography>
                                                    </Box>
                                                )}
{hasProfanityWarnings && (
                                                    <Box className={classes.detailSaveWarningRow}>
                                                        <ErrorIcon color="#92400E" />
                                                        <Typography
                                                            id="writing-style-profanity-warnings"
                                                            className={classes.detailSaveWarning}>
                                                            {i18n.t('STYLE_PROMPT_PROFANITY_WARNING_TITLE')}
                                                        </Typography>
                                                    </Box>
                                                )}
                                                {clientBlockErrors.filter((msg) => !hardBlockErrors.includes(msg)).map((msg, idx) => (
                                                    <Box
                                                        key={`client-${idx}`}
                                                        className={classes.detailSaveErrorRow}>
                                                        <ErrorIcon />
                                                        <Typography
                                                            className={classes.detailSaveError}>
                                                            {msg}
                                                        </Typography>
                                                    </Box>
                                                ))}
                                                {hardBlockErrors.map((msg, idx) => (
                                                    <Box
                                                        key={`server-${idx}`}
                                                        className={classes.detailSaveErrorRow}
                                                        id={
                                                            idx === 0
                                                                ? 'writing-style-hard-block-errors'
                                                                : undefined
                                                        }>
                                                        <ErrorIcon />
                                                        <Typography
                                                            className={classes.detailSaveError}>
                                                            {msg}
                                                        </Typography>
                                                    </Box>
                                                ))}
                                                {(saveError || (isDirty && isEmptyDraft)) && (
                                                    <Box className={classes.detailSaveErrorRow}>
                                                        <ErrorIcon />
                                                        <Typography
                                                            className={classes.detailSaveError}>
                                                            {saveError ||
                                                                i18n.t('STYLE_PROMPT_EMPTY_ERROR')}
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </Box>
                                            <Box style={{ flexShrink: 0, paddingLeft: 8 }}>
                                                <TextLengthCounter
                                                    maxValue={MAX_WRITING_STYLE_DESCRIPTION_LENGTH}
                                                    value={draft}
                                                    colour={true}
                                                />
                                            </Box>
                                        </Box>
                                    </>
                                )}
                            </Box>
                        </PageBody>
                    </Box>

                    <ToastNotification
                        message={i18n.t('WRITING_STYLE_PROMPT_UPDATED')}
                        showToast={showPromptSaveToast}
                        setShowToast={setShowPromptSaveToast}
                        toastType={TOAST_TYPE_SUCCESS}
                    />
                    <ToastNotification
                        message={i18n.t('WRITING_STYLE_PROMPT_UPDATE_FAILED')}
                        showToast={showPromptSaveFailToast}
                        setShowToast={setShowPromptSaveFailToast}
                        toastType={TOAST_TYPE_FAIL}
                    />

                    <div className="custom-toast-container">
                        {Array.from(new Set(toasts.map((toast) => toast.id))).map((id) => {
                            const toast = toasts.find((t) => t.id === id)
                            // Pregen + transcript: same rules as writing-styles list. Writing style: only
                            // when it refers to the style open on this page (ids can collide with doc ids).
                            const isThisWritingStyle =
                                toast?.forWritingStyle === true &&
                                String(toast?.writingStyleId) === String(styleId)
                            const isPregenOrTranscript =
                                toast?.forTranscript === true ||
                                (toast?.forTranscript === false && !toast?.forWritingStyle)
                            const shouldShow =
                                toast?.view_toast === true &&
                                (isThisWritingStyle || isPregenOrTranscript)
                            return shouldShow ? (
                                <StatusToast key={toast.id} {...toast} removeToast={removeToast} />
                            ) : null
                        })}
                    </div>
                    {showRegenerateConfirmation && (
                        <ConfirmationDialogGeneric
                            title={i18n.t('WRITING_STYLE_REGENERATE_TITLE')}
                            message={i18n.t('WRITING_STYLE_REGENERATE_MESSAGE')}
                            confirmLabel={i18n.t('WRITING_STYLE_REGENERATE_BUTTON')}
                            onConfirm={handleRegenerateConfirm}
                            onCancel={() => setShowRegenerateConfirmation(false)}
                            confirmAnalytics="MM-WritingStyles-RegenerateConfirm-DetailPage"
                            cancelAnalytics="MM-WritingStyles-RegenerateCancel-DetailPage"
                            closeAnalytics="MM-WritingStyles-RegenerateClose-DetailPage"
                        />
                    )}
                    {showBackConfirmation && (
                        <ConfirmationDialogGeneric
                            title={i18n.t('STYLE_PROMPT_UNSAVED_TITLE')}
                            message={i18n.t('STYLE_PROMPT_UNSAVED_WARNING')}
                            confirmLabel={i18n.t('STYLE_PROMPT_LEAVE_PAGE')}
                            cancelLabel={i18n.t('STYLE_PROMPT_STAY_ON_PAGE') as string}
                            onConfirm={handleBackConfirm}
                            onCancel={() => {
                                setShowBackConfirmation(false)
                                isPendingPopRef.current = false
                            }}
                            confirmAnalytics="MM-WritingStyles-UnsavedLeave-DetailPage"
                            cancelAnalytics="MM-WritingStyles-UnsavedCancel-DetailPage"
                            closeAnalytics="MM-WritingStyles-UnsavedClose-DetailPage"
                        />
                    )}
                </PageLayout>
            </MuiPickersUtilsProvider>
        </MuiThemeProvider>
    )
}

export default WritingStyleDetailPage
